import { useMemo } from 'react';

import { isPartialPolicyPayload, TypedPolicy } from '../../../types';

import {
  DOCKER_CLEANUP_FORM_INITIAL_VALUES,
  DOCKER_CLEANUP_INITIAL_VALUES,
  DockerCleanupFormData,
  DockerCleanupFormValues,
  DockerCleanupPolicyPayload,
} from './types';
import { msToDuration } from './duration';

function isDockerCleanupPolicyData(
  data: unknown
): data is DockerCleanupPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'cleanOldImages' in data &&
    'cleanImagesAtStorageLimit' in data
  );
}

function deserializePayload(
  data: Partial<DockerCleanupPolicyPayload>,
  defaults: DockerCleanupFormData
): DockerCleanupFormData {
  return {
    cleanupInterval:
      data.cleanupInterval?.value != null
        ? msToDuration(data.cleanupInterval.value)
        : defaults.cleanupInterval,
    cleanOldImages: data.cleanOldImages?.value ?? defaults.cleanOldImages,
    imageMaximumGCAge:
      data.imageMaximumGCAge?.value != null
        ? msToDuration(data.imageMaximumGCAge.value)
        : defaults.imageMaximumGCAge,
    cleanImagesAtStorageLimit:
      data.cleanImagesAtStorageLimit?.value ??
      defaults.cleanImagesAtStorageLimit,
    highThresholdPercent:
      data.highThresholdPercent?.value ?? defaults.highThresholdPercent,
    lowThresholdPercent:
      data.lowThresholdPercent?.value ?? defaults.lowThresholdPercent,
    clearBuildCache: data.clearBuildCache?.value ?? defaults.clearBuildCache,
    minAge:
      data.minAge?.value != null
        ? msToDuration(data.minAge.value)
        : defaults.minAge,
    maxImagesPerInterval:
      data.maxImagesPerInterval?.value ?? defaults.maxImagesPerInterval,
    excludedImages: data.excludedImages?.value ?? defaults.excludedImages,
  };
}

export function useDockerCleanupInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): DockerCleanupFormValues {
  return useMemo(() => {
    const defaults = DOCKER_CLEANUP_INITIAL_VALUES;

    if (
      !policy &&
      templateData &&
      isPartialPolicyPayload<DockerCleanupPolicyPayload>(templateData)
    ) {
      return {
        name: '',
        environmentGroupIds: [],
        ...deserializePayload(templateData, defaults),
      };
    }

    if (!policy) {
      return DOCKER_CLEANUP_FORM_INITIAL_VALUES;
    }

    const data = policy.Data;
    if (!isDockerCleanupPolicyData(data)) {
      return {
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        ...defaults,
      };
    }

    return {
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      ...deserializePayload(data, defaults),
    };
  }, [policy, templateData]);
}
